import { supabase } from '../lib/supabase';

/**
 * Supabase Real-time Service
 * Handles real-time subscriptions for live data updates across admin panel
 */

class RealtimeService {
  constructor() {
    this.channels = new Map();
    this.activeSubscriptions = new Map();
  }

  subscribeToUserActivity(callback) {
    const channelName = 'user_activity_realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_activity'
        },
        (payload) => {
          callback(payload?.new);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  subscribeToUserProfiles(callback) {
    const channelName = 'user_profiles_realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            profile: payload?.new || payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  subscribeToUserSpecificActivity(userId, callback) {
    const channelName = `user_activity_${userId}`;
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_activity',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload?.new);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  subscribeToAdminPresence(adminId, onPresenceChange) {
    const channelName = 'admin_presence';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
    }

    const channel = supabase?.channel(channelName)
      ?.on('presence', { event: 'sync' }, () => {
        const state = channel?.presenceState();
        const activeAdmins = Object.values(state)?.flat();
        onPresenceChange(activeAdmins);
      })
      ?.on('presence', { event: 'join' }, () => {})
      ?.on('presence', { event: 'leave' }, () => {})
      ?.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel?.track({
            user_id: adminId,
            online_at: new Date()?.toISOString()
          });
        }
      });

    this.channels?.set(channelName, channel);

    return () => {
      channel?.untrack();
      this.unsubscribe(channelName);
    };
  }

  subscribeToUserAnalytics(callback) {
    const channelName = 'user_analytics_realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_analytics'
        },
        (payload) => {
          callback(payload?.new);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  subscribeToMultipleTables(callbacks) {
    const unsubscribeFunctions = [];

    Object.entries(callbacks)?.forEach(([tableName, callback]) => {
      const channelName = `${tableName}_multi`;
      
      if (this.channels?.has(channelName)) {
        this.unsubscribe(channelName)?.catch(err => console.warn('Unsubscribe warning:', err));
      }

      const channel = supabase?.channel(channelName)?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: tableName
          },
          (payload) => {
            callback({
              table: tableName,
              event: payload?.eventType,
              data: payload?.new || payload?.old
            });
          }
        )?.subscribe();

      this.channels?.set(channelName, channel);
      unsubscribeFunctions?.push(() => this.unsubscribe(channelName));
    });

    return () => {
      unsubscribeFunctions?.forEach(unsubscribe => unsubscribe());
    };
  }

  async unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      try {
        await supabase?.removeChannel(channel);
      } catch (error) {
        console.error(`Error unsubscribing from ${channelName}:`, error);
      }
      this.channels?.delete(channelName);
    }
  }

  async unsubscribeAll() {
    const channelNames = Array.from(this.channels?.keys());
    for (const channelName of channelNames) {
      await this.unsubscribe(channelName);
    }
  }

  getActiveSubscriptionsCount() {
    return this.channels?.size;
  }

  isChannelActive(channelName) {
    return this.channels?.has(channelName);
  }
}

export const realtimeService = new RealtimeService();
export default realtimeService;